import './ViewDetails.css';
import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip';
import SlBadge from '@shoelace-style/shoelace/dist/react/badge/index.js';
import SlSelect from '@shoelace-style/shoelace/dist/react/select/index.js';
import SlOption from '@shoelace-style/shoelace/dist/react/option/index.js';
import { CDSUiIcons } from '@cencora-design-system/assets/icons';
import { CDSIconWeight, CDSIconSize } from '@cencora-design-system/assets/icons';
import { generateArticlePDF } from './utils/pdfExport';
import DislikeModal from './DislikeModal';
import { useApi } from './useApi';

export default function ViewDetails(props) {
    const { authGet, authPost, authDelete } = useApi();
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [record, setRecord] = useState(location.state?.record || null);
    const [activeTab, setActiveTab] = useState('summary');
    const [loading, setLoading] = useState(!record);
    const [isLiked, setIsLiked] = useState(location.state?.record?.isLike || false);
    const [isDisliked, setIsDisliked] = useState(location.state?.record?.isDislike || false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [copyTooltipText, setCopyTooltipText] = useState("Copy AI Summary");
    const [feedbackMessage, setFeedbackMessage] = useState(null);

    // API URLs from build-time environment variable (fallback to dev for local development)
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const api = `${apiBaseUrl}/records`;
    const latestUpdatesApi = `${apiBaseUrl}/records/latest-updates`;
    const feedbackApi = `${apiBaseUrl}/userfeedback`;

    // Use userId from props (from auth context)
    const userId = props.userId;

    // Sync like/dislike state when record is fetched
    useEffect(() => {
        if (record) {
            setIsLiked(record.isLike || false);
            setIsDisliked(record.isDislike || false);
        }
    }, [record]);

    // Fetch record from latest-updates API if missing history data (opened in new tab or page refresh)

    async function findRecord(id) {
        let page = 1;
        let totalPages = 1;

        while(page <= totalPages) {
            const response = await authGet(`${latestUpdatesApi}?page=${page}&pageSize=1000`);
            const data = await response.json();
            const items = data?.items || [];
            const foundRecord = items.find(item => item.id === id);
            if (foundRecord) {
                return foundRecord;
            }
            totalPages = data?.totalPages;
            page++;
        }
        return null;
    }
    useEffect(() => {
        const loadRecord = async () => {
        const needsFullData = !record
        if (needsFullData && id) {
            setLoading(true);
            try {
                    const foundRecord = await findRecord(id);
                    if (foundRecord) {
                        setRecord(foundRecord);
                    } else {
                        // Fallback: fetch single record if not found in latest updates
                        return authGet(`${api}/${id}`)
                            .then(res => res.json())
                            .then(singleRecord => {
                                setRecord(singleRecord);
                            });
                    }
                }
                catch (err) {
                    console.error("Error fetching record:", err);
                }
                finally{
                    setLoading(false);
                }
        }
    };
        loadRecord();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps


    if (loading) {
        return <div className="container-main"><div className="card-full-w">Loading...</div></div>;
    }


    const title = record?.title;
    const categories = record?.categories || [];
    const region = record?.region;
    const sourceName = record?.sourceName;
    const sourceUrl = record?.sourceUrl;
    const summary = record?.aiSummary || 'No summary is available for this record.';

    const scrapedText = record?.scrapedDate
        ? new Date(record.scrapedDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
        : null;

    const publishedText = record?.publishedDate
        ? new Date(record.publishedDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
        : null;

    const handleDownloadPDF = async () => {
        await generateArticlePDF(record, activeTab);
    };

    const handleCopyAiSummary = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(summary)
            .then(() => {
                setCopyTooltipText("AI Summary copied");
            })
            .catch(err => console.error('Failed to copy AI Summary:', err));
    };

    const resetCopyTooltip = () => {
        setCopyTooltipText("Copy AI Summary");
    };

    const handleLike = async () => {
        try {
            // If already liked, DELETE to remove the like
            if (isLiked) {
                const response = await authDelete(`${feedbackApi}/${id}`);

                if (response.ok) {
                    setIsLiked(false);
                    console.log("Like feedback removed successfully");
                } else {
                    console.error("Failed to remove like feedback:", response.status);
                }
            } else {
                // POST to create like
                const response = await authPost(feedbackApi, {
                    userId: userId,
                    articleId: id,
                    feedbackType: "like"
                });

                if (response.ok) {
                    setIsLiked(true);
                    setIsDisliked(false);
                    setFeedbackMessage("User's feedback updated");
                    setTimeout(() => setFeedbackMessage(null), 4000);
                    console.log("Like feedback submitted successfully");
                } else {
                    console.error("Failed to submit like feedback:", response.status);
                }
            }
        } catch (error) {
            console.error("Error with like feedback:", error);
        }
    };

    const handleDislike = async () => {
        // If already disliked, DELETE to remove the dislike
        if (isDisliked) {
            try {
                const response = await authDelete(`${feedbackApi}/${id}`);

                if (response.ok) {
                    setIsDisliked(false);
                    console.log("Dislike feedback removed successfully");
                } else {
                    console.error("Failed to remove dislike feedback:", response.status);
                }
            } catch (error) {
                console.error("Error removing dislike feedback:", error);
            }
        } else {
            // Open modal to submit dislike with reasons
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmitFeedback = (data) => {
        console.log("Dislike feedback submitted:", data);
        setIsDisliked(true);
        setIsLiked(false);
        setIsModalOpen(false);
        setFeedbackMessage("User's feedback updated");
        setTimeout(() => setFeedbackMessage(null), 4000);
    };

    return <div className="container-main">
        <div className="back-to-dashboard" onClick={() => navigate('/')}>
            <SlIcon src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0yMCAxMkg0bTAgMCA3LTdtLTcgNyA3IDciLz48L3N2Zz4=" />
            <span>Back to Dashboard</span>
        </div>
        {feedbackMessage && (
            <div className="feedback-badge-container-viewdetails">
                <SlBadge className="feedback-badge" size="medium" pill>
                    {feedbackMessage}
                </SlBadge>
            </div>
        )}
        <DislikeModal
            open={isModalOpen}
            onClose={handleCloseModal}
            onSubmit={handleSubmitFeedback}
            articleData={{ id: id, title: title, sourceUrl: sourceUrl }}
            userId={userId}
        />
        <div className="card-full-w">

            <div className="section-heading-container">
                <div className="cus-row">
                    <div className="cus-row-left">
                        <div className="badges-wrapper">
                            {categories.map((category, index) => (
                                <SlBadge key={index} className="category-badge" size="medium" pill>
                                    {category}
                                </SlBadge>
                            ))}
                            {region && (
                                <SlBadge className="category-badge" size="medium" pill>
                                    <span className="country-tag-content">
                                        <SlIcon
                                            src={CDSUiIcons.IconPin}
                                            label="Location"
                                        />
                                        <span>{region}</span>
                                    </span>
                                </SlBadge>
                            )}
                        </div>
                    </div>
                    <div className="cus-row-right">
                        <SlTooltip content="Click the Like button if you think the information in the article is accurate, relevant, and useful.">
                            <span style={{ cursor: 'pointer' }} onClick={handleLike}><SlIcon
                                label="Like"
                                src={isLiked ? "/Icons/Like_Active.svg" : "/Icons/Like.svg"}
                                style={{ color: isLiked ? "#461e96" : "inherit" }}
                            /></span>
                        </SlTooltip>
                        <SlTooltip content="Click to provide feedback if you don't think information in the article is accurate, relevant, and useful.">
                            <span style={{ cursor: 'pointer' }} onClick={handleDislike}><SlIcon
                                label="Dislike"
                                src={isDisliked ? "/Icons/Dislike_Active.svg" : "/Icons/Dislike.svg"}
                                style={{ color: isDisliked ? "#461e96" : "inherit" }}
                            /></span>
                        </SlTooltip>
                        <SlTooltip content={copyTooltipText} hoist>
                            <span style={{ cursor: 'pointer' }} onClick={handleCopyAiSummary} onMouseEnter={resetCopyTooltip}><SlIcon
                                label="Copy AI Summary"
                                src="/Icons/common-file-double-2.svg"
                            /></span>
                        </SlTooltip>
                        <span style={{ cursor: 'pointer' }} onClick={handleDownloadPDF}><SlIcon
                            label="Download"
                            src="/Icons/download_icon.svg"
                        /></span>

                    </div>
                </div>

                {title && (
                    <div className="section-heading-1">
                        {title}
                    </div>
                )}

                <div className="cus-row">
                    <div className="cus-row-left">
                        {sourceName && sourceUrl && (
                            <span><SlIcon src="/Icons/UrlIcon.svg" /><a href={sourceUrl} target="_blank" rel="noreferrer">{sourceName}</a></span>
                        )}
                        {scrapedText && (
                            <span><SlIcon src="/Icons/scrape_time.svg" />Scraped: {scrapedText}</span>
                        )}
                        {publishedText ? (
                            <span><SlIcon src={CDSUiIcons.IconCalendar} />Published: {publishedText}</span>
                        ) : <span><SlIcon src={CDSUiIcons.IconCalendar} />Published: N/A</span>
                        }
                    </div>

                </div>


            </div>

            {/* section heading end here */}

            <ul className="tab">
                <li><span onClick={() => setActiveTab('summary')} className={activeTab === 'summary' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                    <SlIcon label="Icon label" src={CDSUiIcons.IconListBullets} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                    <span>Summary</span>
                </span></li>
                <li><span onClick={() => setActiveTab('latest')} className={activeTab === 'latest' ? 'active' : ''} style={{ cursor: 'pointer' }}>
                    <SlIcon label="Icon label" src={CDSUiIcons.IconBookEdit} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                    <span>Latest updates</span></span></li>
                <li><span onClick={() => setActiveTab('past')} className={activeTab === 'past' ? 'active' : ''} style={{ cursor: 'pointer' }}>                    <SlIcon label="Icon label" src={CDSUiIcons.IconTimeClockCircle} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                    <span>Past updates</span></span></li>
            </ul>

            {activeTab === 'summary' && (
                <>
                    <div className="form-sort-result-container">
                        <div className="heading-sort-result">Summary <span>(AI Generated)</span></div>
                    </div>
                    <div className="history-container">
                        <ul className="history-listing">
                            <li>
                                <p className="listing-para">
                                    {summary}
                                </p>
                            </li>
                        </ul>
                    </div>
                </>
            )}

            {activeTab === 'latest' && <LatestUpdates record={record} />}

            {activeTab === 'past' && <PastUpdates record={record} />}
        </div>
    </div>
}

function LatestUpdates({ record }) {
    const changeHistory = record?.changeHistory || [];
    const latestUpdates = record?.latestUpdates || [];

    // Filter changes from the past 30 days (for changeHistory fallback)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentChanges = changeHistory.filter(change => {
        const changeDate = new Date(change.changedAt);
        return changeDate >= thirtyDaysAgo;
    });

    // Priority: 1) latestUpdates from record, 2) changeHistory fallback
    const updatesToShow = latestUpdates.length > 0 ? latestUpdates : recentChanges;

    // Sort by changedAt date in descending order (newest first)
    const sortedUpdates = [...updatesToShow].sort((a, b) =>
        new Date(b.changedAt) - new Date(a.changedAt)
    );

    return (
        <>
            <div className="form-sort-result-container">
                <div className="heading-sort-result">Latest updates <span>(AI generated)</span></div>
            </div>
            <div className="history-container">
                <ul className="history-listing">
                    {sortedUpdates.length > 0 ? (
                        sortedUpdates.map((change, index) => (
                            <li key={index}>
                                <p className="listing-date">
                                    <i className="fa fa-calendar-o" aria-hidden="true"></i> {new Date(change.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                </p>
                                <p className="listing-para">{change.changeDescription}</p>
                            </li>
                        ))
                    ) : (
                        <li>
                            <p className="listing-para">No latest updates to be displayed.</p>
                        </li>
                    )}
                </ul>
            </div>
        </>
    );
}

function PastUpdates({ record }) {
    const [filterRange, setFilterRange] = useState('all');
    const pastHistory = record?.pastHistory || [];

    const filterOptions = [
        { value: 'all', label: "All" },
        { value: '31-60', label: "31 days to 60 days" },
        { value: '61-90', label: "61 days to 90 days" },
        { value: '91-120', label: "91 days to 120 days" },
        { value: '121-180', label: "121 days to 180 days" },
        { value: '180+', label: "> 180 days" }
    ];

    // Filter changes based on selected date range
    const filteredUpdates = pastHistory.filter(change => {
        const changeDate = new Date(change.changedAt);
        const today = new Date();
        const daysDiff = Math.floor((today - changeDate) / (1000 * 60 * 60 * 24));

        switch (filterRange) {
            case '31-60':
                return daysDiff >= 31 && daysDiff <= 60;
            case '61-90':
                return daysDiff >= 61 && daysDiff <= 90;
            case '91-120':
                return daysDiff >= 91 && daysDiff <= 120;
            case '121-180':
                return daysDiff >= 121 && daysDiff <= 180;
            case '180+':
                return daysDiff > 180;
            case 'all':
            default:
                return true; // Show all past history
        }
    });

    // Sort by changedAt date in ascending order (oldest to newest)
    const sortedUpdates = [...filteredUpdates].sort((a, b) =>
        new Date(a.changedAt) - new Date(b.changedAt)
    );

    // Get label for empty state message
    const getFilterLabel = () => {
        const option = filterOptions.find(opt => opt.value === filterRange);
        return option ? option.label : 'selected range';
    };

    return (
        <>
            <div className="form-sort-result-container">
                <div className="heading-sort-result">Past updates <span>(AI generated)</span></div>
                <div className="filter-dropdown">
                    <label className="filter-label">Select date range:</label>
                    <SlSelect
                        value={filterRange}
                        onSlChange={(e) => setFilterRange(e.target.value)}
                        className="past-updates-filter"
                        size="medium"
                    >
                        {filterOptions.map(option => (
                            <SlOption key={option.value} value={option.value}>
                                {option.label}
                            </SlOption>
                        ))}
                    </SlSelect>
                </div>
            </div>
            <div className="history-container">
                <ul className="history-listing">
                    {sortedUpdates.length > 0 ? (
                        sortedUpdates.map((change, index) => (
                            <li key={index}>
                                <p className="listing-date">
                                    <i className="fa fa-calendar-o" aria-hidden="true"></i> {new Date(change.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                </p>
                                <p className="listing-para">{change.changeDescription}</p>
                            </li>
                        ))
                    ) : (
                        <li>
                            <p className="listing-para">No past updates to be displayed.</p>
                        </li>
                    )}
                </ul>
            </div>
        </>
    );
}